'use client'

/**
 * Stage 33/37 — candidate-pair drawer, opened from the scenarios 6–7 table.
 *
 * It is a drawer inside /analytics, not a page: no route is created for a pair or a
 * cluster. Clicking another possible link swaps the drawer's own `pairId` state, so the
 * same drawer re-renders with that candidate_pair — again without navigating.
 *
 * «Открыть полный кейс» is the only navigation, and it targets the existing
 * /cases/[id] route with `canonical_pair_id` + `scenario_id=6|7`.
 *
 * Stage 37: the header names the case and carries a short id only; the body renders the
 * simplified analyst card (see `candidate-pair-detail.tsx`), and the cluster is no longer
 * a section of its own — competing candidates appear as the collapsed «Другие возможные
 * связи», their diagnostics as «Технические сведения».
 */
import { useEffect, useState } from 'react'
import { useT } from '@/lib/i18n'
import {
  useCandidatePair,
  useCandidatePairCluster,
  useCandidatePairExplanations,
  useCandidateClusterPairs,
} from '../cross-unit.hooks'
import { buildFullCaseHref } from '../cross-unit.presenters'
import type { CandidateClusterPairSort } from '../cross-unit.types'
import { CandidatePairDetailBody, CandidatePairHeader } from './candidate-pair-detail'
import { RelatedLinks } from './related-links'
import { CandidateError } from './cross-unit-primitives'

const ALT_PAGE_SIZE = 25
const ALT_SORT: CandidateClusterPairSort = 'anomaly_desc'

export function CandidatePairDrawer({
  pairId,
  scenarioId,
  onClose,
}: {
  pairId: string | null
  scenarioId: number
  onClose: () => void
}) {
  const t = useT()
  // Local navigation between competing candidates — the drawer owns which pair it shows.
  const [activePairId, setActivePairId] = useState<string | null>(pairId)
  const [altPage, setAltPage] = useState(1)
  const [showAlternatives, setShowAlternatives] = useState(false)

  // Reset during render (not in an effect) when the drawer is opened on another pair —
  // same pattern the scenarios 1-5 view uses for its filter resets.
  const [lastOpened, setLastOpened] = useState(pairId)
  if (pairId !== lastOpened) {
    setLastOpened(pairId)
    setActivePairId(pairId)
    setAltPage(1)
    setShowAlternatives(false)
  }

  useEffect(() => {
    if (!pairId) return
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', h)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', h)
      document.body.style.overflow = ''
    }
  }, [pairId, onClose])

  const detail = useCandidatePair(activePairId)
  const explanations = useCandidatePairExplanations(activePairId)
  const cluster = useCandidatePairCluster(activePairId, showAlternatives)
  const groupId = cluster.data?.stableCaseGroupId ?? detail.data?.cluster?.stableCaseGroupId ?? null
  const alternatives = useCandidateClusterPairs(groupId, altPage, ALT_PAGE_SIZE, ALT_SORT, showAlternatives)

  if (!pairId) return null

  const openAlternative = (id: string) => {
    setActivePairId(id)
    setAltPage(1)
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-[820px] flex-col overflow-y-auto bg-[var(--bg-canvas)] shadow-xl"
      >
        <CandidatePairHeader
          pairId={activePairId ?? pairId}
          scenarioId={scenarioId}
          fullCaseHref={activePairId ? buildFullCaseHref(activePairId, scenarioId) : undefined}
          onClose={onClose}
        />

        <div className="px-4 py-4">
          {detail.isLoading && (
          <p className="text-[12px] text-[var(--text-tertiary)]">{t('analytics.pair.loadingCase')}</p>
        )}
          {detail.isError && (
            <CandidateError
              message={
            detail.error instanceof Error
              ? detail.error.message
              : t('analytics.pair.unavailable')
          }
              code={(detail.error as { errorCode?: string } | undefined)?.errorCode}
            />
          )}
          {detail.data && (
            <CandidatePairDetailBody
              detail={detail.data}
              explanations={explanations.data ?? detail.data.explanations}
              scenarioId={scenarioId}
              relatedSlot={
                <RelatedLinks
                  open={showAlternatives}
                  onToggle={() => setShowAlternatives((v) => !v)}
                  items={alternatives.data?.items ?? []}
                  pagination={alternatives.data?.pagination}
                  onPage={setAltPage}
                  onOpenPair={openAlternative}
                  currentPairId={activePairId ?? undefined}
                  current={{
                    importShipmentId: detail.data.importLeg?.shipmentId as string | null | undefined,
                    exportShipmentId: detail.data.exportLeg?.shipmentId as string | null | undefined,
                    daysBetween: detail.data.pairing?.daysBetween ?? null,
                  }}
                  isLoading={alternatives.isLoading}
                />
              }
            />
          )}
        </div>
      </aside>
    </div>
  )
}
